/**
 * FeatureSummaryRibbon — horizontal aggregator chip-bar above the drawing.
 *
 * Renders one chip per DFM feature kind found in `features`, with the count
 * and a small severity dot (amber = warn, red = alert). Clicking a chip
 * toggles `kindFilter` so the overlay can spotlight just that family.
 *
 * Props
 * - features:    array of feature_annotations {kind, severity, value_mm, label, quantity}
 * - theme:       FEATURE_THEME map (color + icon + label per kind)
 * - kindFilter:  current filter ('' = no filter)
 * - setKindFilter: setter
 */
export default function FeatureSummaryRibbon({ features, theme, kindFilter, setKindFilter }) {
  if (!features || features.length === 0) return null;

  // Aggregate by kind, summing 'quantity' (defaults to 1 per feature)
  const byKind = new Map();
  for (const f of features) {
    const k = f.kind || "hole";
    const sev = (f.severity || "info").toLowerCase();
    const qty = Math.max(1, parseInt(f.quantity, 10) || 1);
    const entry = byKind.get(k) || { qty: 0, alerts: 0, warns: 0 };
    entry.qty += qty;
    if (sev === "alert") entry.alerts += 1;
    else if (sev === "warn") entry.warns += 1;
    byKind.set(k, entry);
  }

  const items = Array.from(byKind.entries()).sort((a, b) => b[1].qty - a[1].qty);
  const totalAlerts = items.reduce((acc, [, v]) => acc + v.alerts, 0);
  const totalWarns = items.reduce((acc, [, v]) => acc + v.warns, 0);

  return (
    <div className="tech-card px-4 py-3" data-testid="feature-summary-ribbon">
      <div className="flex items-center gap-2 mb-2">
        <div className="label-tech text-pink-300 text-[10px]">DFM · Feature ({features.length})</div>
        {totalAlerts > 0 && (
          <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border border-rose-500/40 bg-rose-500/10 text-rose-200" data-testid="ribbon-alerts">
            {totalAlerts} alert
          </span>
        )}
        {totalWarns > 0 && (
          <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-200" data-testid="ribbon-warns">
            {totalWarns} warn
          </span>
        )}
        <div className="ml-auto text-[9px] text-slate-500 font-mono uppercase tracking-wider">
          Click per evidenziare
        </div>
      </div>
      <div className="flex items-center flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => setKindFilter("")}
          className={`px-2.5 py-1 rounded-sm text-[11px] border transition-colors ${kindFilter === "" ? "border-pink-500/60 text-pink-200 bg-pink-500/15" : "border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-200"}`}
          data-testid="ribbon-chip-all"
        >
          Tutte <span className="opacity-70 font-mono-data ml-1">({features.length})</span>
        </button>
        {items.map(([kind, info]) => {
          const t = theme[kind] || { color: "#94A3B8", icon: "•", label: kind };
          const active = kindFilter === kind;
          return (
            <button
              key={`chip-${kind}`}
              type="button"
              onClick={() => setKindFilter(active ? "" : kind)}
              className={`group relative px-2.5 py-1 rounded-sm text-[11px] border transition-all flex items-center gap-1.5 ${active ? "shadow-md" : "hover:scale-105"}`}
              style={{
                borderColor: active ? t.color : "#334155",
                color: active ? "#fff" : "#cbd5e1",
                backgroundColor: active ? `${t.color}22` : "transparent",
              }}
              data-testid={`ribbon-chip-${kind}`}
              title={`${t.label} · ${info.qty} totali${info.alerts ? ` · ${info.alerts} alert` : ""}${info.warns ? ` · ${info.warns} warn` : ""}`}
            >
              <span aria-hidden="true" style={{ color: t.color }} className="text-sm leading-none">{t.icon}</span>
              <span className="font-mono-data">{t.label}</span>
              <span className="opacity-70 font-mono-data">×{info.qty}</span>
              {info.alerts > 0 && <span className="w-1.5 h-1.5 rounded-full bg-rose-400" aria-label="alert"/>}
              {info.warns > 0 && info.alerts === 0 && <span className="w-1.5 h-1.5 rounded-full bg-amber-400" aria-label="warn"/>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
